import { createElement, FC, ReactNode, SVGProps, useMemo } from "react";

import find from "lodash/find";
import map from "lodash/map";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import Cleaner from "@/icons/__generated__/Cleaner/Cleaner24";
import Health from "@/icons/__generated__/Health/Health32";
import { useListingData } from "~/context/ListingPageDataContext";
import { cn } from "~/lib/utils";
import { ICleaningInfo } from "~/types/api-types";
import { loadDynamicIcon } from "~/utils/loadDynamicIcon";

import { ReadMoreText } from "../../utils/ReadMoreToggle";

import cleaningPolicyData from "./data/cleaning-types";

const cleaningPolicy: ICleaningPolicy =
  cleaningPolicyData satisfies ICleaningPolicy;

interface HealthAndSafetyProps {
  cleaningInfo?: ICleaningInfo;
}

interface ICleaningPolicyItems {
  heading: ReactNode;
  listingHeader?: ReactNode;
  listingSubheader?: ReactNode;
  icon?: string;
  items: { key: string; label: ReactNode }[];
}

interface ICleaningPolicy {
  cleaning_measures: ICleaningPolicyItems;
  protective_gear: ICleaningPolicyItems;
  physical_distance: ICleaningPolicyItems;
  signage: ICleaningPolicyItems;
}

export const HealthAndSafety: FC<HealthAndSafetyProps> = ({ cleaningInfo }) => {
  const intl = useIntl();
  const {
    data: { listingId },
  } = useListingData();
  const translatedMsgs = defineMessages({
    enhanced: {
      id: "listing.standard_cleaning.enhanced",
      defaultMessage: "Enhanced",
    },
    cleaning_solution: {
      id: "listing.standard_cleaning.cleaning_solution",
      defaultMessage: "cleaing solution",
    },
    enhanced_image: {
      id: "listing.standard_cleaning.enhanced_image",
      defaultMessage: "enhanced-badge",
    },
  });

  const memoizedIcons = useMemo(() => {
    // store icons by their actual name, not the key
    const icons = {
      mask: cleaningPolicy.protective_gear.icon
        ? loadDynamicIcon(cleaningPolicy.protective_gear.icon)
        : null,
      "physical-distance": cleaningPolicy.physical_distance.icon
        ? loadDynamicIcon(cleaningPolicy.physical_distance.icon)
        : null,
      signage: cleaningPolicy.signage.icon
        ? loadDynamicIcon(cleaningPolicy.signage.icon)
        : null,
    };
    return icons;
  }, []);

  return (
    <div className="tw-pt-40" data-testing-id="enhancedHealthSectionContainer">
      <div className="CleaningSectionHeader">
        <div
          className="tw-w-full tw-flex tw-items-center tw-gap-12"
          data-testing-id="cleaningPolicyDescriptionLabel"
        >
          {cleaningInfo?.has_enhanced_cleaning && (
            <Health
              role="img"
              aria-label={intl.formatMessage(translatedMsgs.enhanced_image)}
            />
          )}
          <Text
            element="h2"
            typeface="headline-2-m"
            className={cn("tw-my-12", {
              "enhanced-cleaning col-xs-10":
                cleaningInfo?.has_enhanced_cleaning,
            })}
          >
            <FormattedMessage
              id="listing.standard_cleaning.health_safety"
              defaultMessage="{enhanced} Health and Safety Measures"
              values={{
                enhanced: cleaningInfo?.has_enhanced_cleaning
                  ? intl.formatMessage(translatedMsgs.enhanced)
                  : "",
              }}
            />
          </Text>
        </div>
      </div>
      <div className="tw-mb-24">
        {cleaningInfo?.description ? (
          <ReadMoreText
            text={cleaningInfo.description}
            openText={
              <FormattedMessage
                id="listing.standard_cleaning.read_more"
                defaultMessage="Read more"
              />
            }
            closeText={
              <FormattedMessage
                id="listing.standard_cleaning.hide"
                defaultMessage="Hide"
              />
            }
          />
        ) : (
          <span>
            <FormattedMessage
              id="listing.standard_cleaning.description"
              defaultMessage="To help limit the spread of the coronavirus, Peerspace has provided guidelines for hosts so guests can book with peace of mind. {link}"
              values={{
                link: (
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href="//book.peerspace.com/covid-19/overview"
                    className="tw-underline"
                    key="learn-more"
                  >
                    <FormattedMessage
                      id="listing.standard_cleaning.description_learn_more"
                      defaultMessage="Learn More"
                    />
                  </a>
                ),
              }}
            />
          </span>
        )}
      </div>
      <div>
        <Accordion
          className="tw-w-full"
          collapsible
          type="single"
          onValueChange={() => {
            window.analyticsIntegrations.trackEvent("amenities expanded", {
              listingId: listingId,
            });
          }}
        >
          <AccordionItem value={`item-cleaning-faqs`} showDivider={true}>
            <AccordionTrigger className="tw-w-full">
              <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                <div className="tw-flex tw-items-center tw-py-16">
                  <div className="img-container tw-mr-16">
                    <Cleaner
                      role="img"
                      aria-label={`health-and-safety-${intl.formatMessage(translatedMsgs.cleaning_solution)}`}
                    />
                  </div>
                  <div>
                    <Text element="h3" typeface="headline-1-m">
                      <FormattedMessage
                        id="listing.standard_cleaning.cleaning_protocol"
                        defaultMessage="Cleaning protocol"
                      />
                    </Text>
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="">
              {!!cleaningInfo?.cleaning_measures.length && (
                <div className="tw-mb-24">
                  <Text typeface="body-2-m">
                    <FormattedMessage
                      id="listing.standard_cleaning.enhance_cleaning_measures"
                      defaultMessage="Enhanced cleaning measures:"
                    />
                  </Text>
                  <ul className="tw-mt-16 tw-mb-24 tw-ml-40">
                    {map(cleaningInfo.cleaning_measures, (measure, index) => {
                      const measureObj = find(
                        cleaningPolicy.cleaning_measures.items,
                        ["key", measure],
                      );
                      return (
                        <li key={index}>
                          <Text className="tw-pb-24 tw-text-gray-600">
                            {measureObj?.label || ""}
                          </Text>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              <Text typeface="body-2-m">
                <FormattedMessage
                  id="listing.standard_cleaning.cleaning_title"
                  defaultMessage="All hosts are required to do the following prior to each booking:"
                />
              </Text>
              <ul className="tw-mt-16 tw-mb-24 tw-ml-40">
                <Text className="tw-pb-24 tw-text-gray-600">
                  <FormattedMessage
                    id="listing.standard_cleaning.cleaning_list_1"
                    defaultMessage="Sweep, mop, vacuum and clean the space."
                  />
                </Text>
                <Text className="tw-pb-24 tw-text-gray-600">
                  <FormattedMessage
                    id="listing.standard_cleaning.cleaning_list_2"
                    defaultMessage="Supply a hand washing station with soap, warm water, and paper towels or hand sanitizer with at least 60% alcohol."
                  />
                </Text>
                <Text className="tw-pb-24 tw-text-gray-600">
                  <FormattedMessage
                    id="listing.standard_cleaning.cleaning_list_3"
                    defaultMessage="Clean common areas allowing guest access including bathrooms, kitchens, and entrances."
                  />
                </Text>
                <Text className="tw-pb-24 tw-text-gray-600">
                  <FormattedMessage
                    id="listing.standard_cleaning.cleaning_list_4"
                    defaultMessage="Collect and clean dishes, silverware, and other provided host amenities, if applicable."
                  />
                </Text>
                <Text className="tw-pb-24 tw-text-gray-600">
                  <FormattedMessage
                    id="listing.standard_cleaning.cleaning_list_5"
                    defaultMessage="Remove garbage and add new lining to cans."
                  />
                </Text>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      <div>
        {map(
          [
            [cleaningInfo?.protective_gear, cleaningPolicy.protective_gear],
            [cleaningInfo?.physical_distance, cleaningPolicy.physical_distance],
            [cleaningInfo?.signage, cleaningPolicy.signage],
          ],
          (value) => {
            const cleaningInfoItems = value[0] as string[] | undefined;
            const cleaningPolicyItem = value[1] as ICleaningPolicyItems;
            if (!cleaningPolicyItem) return null;
            if (cleaningInfoItems?.length) {
              const icon = cleaningPolicyItem.icon || "";
              const listingHeader = cleaningPolicyItem.listingHeader || "";
              const listingSubheader =
                cleaningPolicyItem.listingSubheader || "";
              return (
                <Accordion
                  key={icon}
                  className="tw-w-full"
                  collapsible
                  type="single"
                >
                  <AccordionItem
                    value={`item-cleaning-faqs`}
                    showDivider={true}
                  >
                    <AccordionTrigger className="tw-w-full">
                      <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                        <div className="tw-flex tw-items-center tw-py-16">
                          <div className="img-container tw-mr-16">
                            {icon &&
                              memoizedIcons[
                                cleaningPolicyItem.icon as keyof typeof memoizedIcons
                              ] &&
                              createElement(
                                memoizedIcons[
                                  cleaningPolicyItem.icon as keyof typeof memoizedIcons
                                ] as React.ComponentType,
                                {
                                  role: "img",
                                  "aria-label": `health-and-safety-icon-${icon}`,
                                } as SVGProps<SVGSVGElement>,
                              )}
                          </div>
                          <div>
                            <Text element="h3" typeface="headline-1-m">
                              {listingHeader}
                            </Text>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="tw-mb-24">
                        <Text typeface="body-2-m">{listingSubheader}</Text>
                        <ul className="tw-mt-16 tw-mb-24 tw-ml-40">
                          {map(cleaningInfoItems, (item, index) => {
                            const itemObj = cleaningPolicyItem.items.find(
                              (x) => x.key === item,
                            );
                            return (
                              <li key={index}>
                                <Text className="tw-pb-24 tw-text-gray-600">
                                  {itemObj?.label || ""}
                                </Text>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              );
            }
            return null;
          },
        )}
      </div>
    </div>
  );
};
